import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Briefcase, ListChecks, FileText, Plus, Send, Gauge, Rocket,
} from "lucide-react";
import { api } from "@/api/client";
import { useSession } from "@/context/SessionContext";
import CareerAutoPilot from "@/components/dashboard/CareerAutoPilot";

/* Career workspace — legacy `career_hub` engine (POST /api/career/hub):
   Job/Task Board (milestone + profile_map), Applications (service_request),
   Resume Scan (analyze_resume). Severed from the profile editor per directive. */
export default function CareerDesk({ onBack }) {
  const { user } = useSession();
  const userId = user?.user_id || "founder_1";
  const [tab, setTab] = useState("auto");

  const call = (body) => api.post("/career/hub", { user_id: userId, ...body }).then((r) => r.data);

  /* ---- Job / Task Board (milestones) ---- */
  const [tasks, setTasks] = useState([]);
  const [taskTitle, setTaskTitle] = useState("");
  const [boardBusy, setBoardBusy] = useState(false);
  const loadBoard = () => call({ op: "profile_map" }).then((d) => setTasks(d.milestones || [])).catch(() => {});
  useEffect(() => { loadBoard(); /* eslint-disable-next-line */ }, []);

  const addTask = async () => {
    if (!taskTitle.trim()) { toast.error("Add a job title / task"); return; }
    setBoardBusy(true);
    try {
      await call({ op: "milestone", role: taskTitle.trim() });
      setTaskTitle("");
      await loadBoard();
      toast.success("Added to board");
    } catch { toast.error("Could not add"); } finally { setBoardBusy(false); }
  };

  /* ---- Applications (service_request) ---- */
  const [jobDesc, setJobDesc] = useState("");
  const [apps, setApps] = useState([]);
  const [appBusy, setAppBusy] = useState(false);
  const submitApp = async () => {
    if (!jobDesc.trim()) { toast.error("Paste a job description"); return; }
    setAppBusy(true);
    try {
      const d = await call({ op: "service_request", job_desc: jobDesc.trim() });
      setApps((a) => [{ ...d.request, preview: jobDesc.trim().slice(0, 80) }, ...a]);
      setJobDesc("");
      toast.success("Application submitted");
    } catch { toast.error("Submit failed"); } finally { setAppBusy(false); }
  };

  /* ---- Resume Scan (analyze_resume) ---- */
  const [resume, setResume] = useState("");
  const [role, setRole] = useState("");
  const [scan, setScan] = useState(null);
  const [scanBusy, setScanBusy] = useState(false);
  const runScan = async () => {
    if (!resume.trim()) { toast.error("Paste your resume text"); return; }
    setScanBusy(true);
    try {
      const d = await call({ op: "analyze_resume", role: role.trim(), resume_text: resume.trim() });
      setScan(d);
      toast.success("Resume analyzed");
    } catch { toast.error("Scan failed"); } finally { setScanBusy(false); }
  };

  const TABS = [
    { k: "auto", label: "Auto-Pilot", Icon: Rocket },
    { k: "board", label: "Board", Icon: ListChecks },
    { k: "apps", label: "Apply", Icon: Send },
    { k: "scan", label: "Scan", Icon: FileText },
  ];

  return (
    <div className="absolute inset-0 z-30 flex flex-col" style={{ backgroundColor: "#FFD700" }} data-testid="career-desk">
      <header className="shrink-0 px-4 pt-4 pb-3 flex items-center gap-3">
        <button onClick={onBack} data-testid="career-back-btn" aria-label="Back" className="w-10 h-10 rounded-full bg-white border-2 border-charcoal grid place-items-center shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none"><ArrowLeft size={18} strokeWidth={2.4} /></button>
        <div className="leading-none">
          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-charcoal/60">Career</p>
          <h1 className="font-serif text-2xl text-charcoal flex items-center gap-1.5"><Briefcase size={18} /> Jobs & Pipeline</h1>
        </div>
      </header>

      {/* Tab strip */}
      <div className="shrink-0 px-4 pb-3">
        <div className="grid grid-cols-4 gap-2 bg-white border-2 border-charcoal rounded-2xl p-1 shadow-[3px_3px_0px_#1A1A1A]">
          {TABS.map(({ k, label, Icon }) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`career-tab-${k}`}
              className={`flex items-center justify-center gap-1 font-mono text-[8px] font-black uppercase tracking-widest rounded-xl py-2 ${tab === k ? "bg-charcoal text-gold" : "text-charcoal/70"}`}>
              <Icon size={11} /> {label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar px-4 pb-6 space-y-3">
        {tab === "auto" && <CareerAutoPilot />}
        {tab === "board" && (
          <>
            <div className="border-4 border-charcoal rounded-2xl bg-white p-3 shadow-[3px_3px_0px_#1A1A1A]" data-testid="career-board-add">
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/70 mb-2">New Job / Task</p>
              <div className="flex gap-2">
                <input value={taskTitle} onChange={(e) => setTaskTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addTask()} data-testid="career-task-input" placeholder="e.g. Apply — Senior Designer @ Acme" className="flex-1 bg-cream border-2 border-charcoal rounded-xl px-3 py-2.5 font-mono text-xs outline-none focus:ring-2 focus:ring-charcoal" />
                <button onClick={addTask} disabled={boardBusy} data-testid="career-task-add-btn" className="shrink-0 w-11 grid place-items-center bg-charcoal text-gold border-2 border-charcoal rounded-xl shadow-[2px_2px_0px_#1A1A1A] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none disabled:opacity-60">{boardBusy ? <Loader2 size={16} className="animate-spin" /> : <Plus size={18} strokeWidth={3} />}</button>
              </div>
            </div>
            <div className="space-y-2" data-testid="career-board-list">
              {tasks.length === 0 ? (
                <p className="font-mono text-[10px] text-charcoal/70 text-center py-8">No jobs on the board yet. Add your first above.</p>
              ) : tasks.map((t, i) => (
                <div key={t.id || i} className="border-2 border-charcoal rounded-xl bg-white p-3 shadow-[2px_2px_0px_#1A1A1A] flex items-center gap-2" data-testid={`career-task-${i}`}>
                  <span className="w-7 h-7 shrink-0 rounded-lg bg-charcoal text-gold grid place-items-center font-mono text-[10px] font-black">{i + 1}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-mono text-xs font-black text-charcoal truncate">{t.role || "Untitled task"}</p>
                    <p className="font-mono text-[8px] text-charcoal/70">{(t.at || "").slice(0, 10)}</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "apps" && (
          <>
            <div className="border-4 border-charcoal rounded-2xl bg-white p-3 shadow-[3px_3px_0px_#1A1A1A]" data-testid="career-app-form">
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/70 mb-2">Submit an Application</p>
              <textarea value={jobDesc} rows={4} onChange={(e) => setJobDesc(e.target.value)} data-testid="career-jobdesc-input" placeholder="Paste the job description / role details…" className="w-full bg-cream border-2 border-charcoal rounded-xl px-3 py-2.5 font-mono text-xs outline-none focus:ring-2 focus:ring-charcoal resize-none" />
              <button onClick={submitApp} disabled={appBusy} data-testid="career-app-submit-btn" className="mt-2 w-full flex items-center justify-center gap-2 bg-charcoal text-gold font-black font-mono text-[11px] uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">{appBusy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Submit Application</button>
            </div>
            <div className="space-y-2" data-testid="career-app-list">
              {apps.map((a, i) => (
                <div key={a.id || i} className="border-2 border-charcoal rounded-xl bg-white p-3 shadow-[2px_2px_0px_#1A1A1A]" data-testid={`career-app-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono text-[8px] font-black uppercase tracking-widest bg-emerald-500 text-white border border-charcoal rounded-full px-2 py-0.5">{a.status || "received"}</span>
                    <span className="font-mono text-[8px] text-charcoal/70">{(a.at || "").slice(0, 10)}</span>
                  </div>
                  <p className="mt-1.5 font-mono text-[11px] text-charcoal/80 leading-snug">{a.preview}…</p>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "scan" && (
          <>
            <div className="border-4 border-charcoal rounded-2xl bg-white p-3 shadow-[3px_3px_0px_#1A1A1A]" data-testid="career-scan-form">
              <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-charcoal/70 mb-2">Resume Analyzer</p>
              <input value={role} onChange={(e) => setRole(e.target.value)} data-testid="career-scan-role" placeholder="Target role (optional)" className="w-full mb-2 bg-cream border-2 border-charcoal rounded-xl px-3 py-2.5 font-mono text-xs outline-none focus:ring-2 focus:ring-charcoal" />
              <textarea value={resume} rows={5} onChange={(e) => setResume(e.target.value)} data-testid="career-scan-resume" placeholder="Paste your resume text…" className="w-full bg-cream border-2 border-charcoal rounded-xl px-3 py-2.5 font-mono text-xs outline-none focus:ring-2 focus:ring-charcoal resize-none" />
              <button onClick={runScan} disabled={scanBusy} data-testid="career-scan-btn" className="mt-2 w-full flex items-center justify-center gap-2 bg-charcoal text-gold font-black font-mono text-[11px] uppercase tracking-widest py-3 rounded-full border-2 border-charcoal shadow-[3px_3px_0px_#1A1A1A] active:translate-x-[2px] active:translate-y-[2px] active:shadow-none disabled:opacity-60">{scanBusy ? <Loader2 size={15} className="animate-spin" /> : <Gauge size={15} />} Analyze Resume</button>
            </div>
            {scan && (
              <div className="border-4 border-charcoal rounded-2xl bg-charcoal p-4 shadow-[3px_3px_0px_#1A1A1A] text-center" data-testid="career-scan-result">
                <p className="font-mono text-[9px] uppercase tracking-[0.3em] text-gold">Match Score</p>
                <p className="font-serif text-5xl text-white tabular-nums mt-1">{scan.score}</p>
                <p className="font-mono text-[9px] text-white/70 mt-1">{scan.word_count} words · {scan.role || "no role set"}</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
